/**
 * Security tab — password + signed-in devices.
 *
 * The password form is NOT on the page until asked for: it is a collapsed row with a
 * "Change password" button. (That also means the browser never sees a stray
 * password field to autofill.) Errors are inline on the field they belong to:
 * a wrong current password under "Current password", a weak new one under "New
 * password", and the server's 429 turns the button into a live countdown.
 *
 * A successful change makes the server sign out every OTHER device, so the devices
 * list below is re-fetched (its `key` is bumped) rather than left showing sessions
 * that no longer exist.
 */
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Eye, EyeOff, KeyRound, MonitorSmartphone } from 'lucide-react';
import React, { useState } from 'react';

import AccountCard from '@/components/account/AccountCard';
import ActiveSessions from '@/components/users/ActiveSessions';
import { formatWait, retryAfterSeconds, useCooldown } from '@/hooks/useCooldown';
import apiService from '@/services/api.service';
import { PASSWORD_MIN, passwordProblem } from '@/utils/passwordPolicy';

import { extractError, Notify } from './shared';

interface Props {
  hasPassword: boolean;
  /** Called after a first-time password is set (a Google-only account now has one). */
  onPasswordSet: () => void;
  notify: Notify;
}

const EMPTY = { current: '', next: '', confirm: '' };

const PasswordCard: React.FC<Props & { onChanged: () => void }> = ({
  hasPassword,
  onPasswordSet,
  onChanged,
  notify,
}) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(EMPTY);
  const [touched, setTouched] = useState(false);
  // One visibility toggle per field, so revealing one doesn't expose the others.
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState<{
    current?: string;
    next?: string;
    form?: string;
  }>({});
  // Chrome fills a "current password" field with the saved login password on mount;
  // keeping it read-only until the user focuses it stops that surprise (existing behaviour).
  const [currentUnlocked, setCurrentUnlocked] = useState(false);
  const cooldown = useCooldown();

  const close = () => {
    setOpen(false);
    setValues(EMPTY);
    setTouched(false);
    setServerErrors({});
    setCurrentUnlocked(false);
    setShow({ current: false, next: false, confirm: false });
  };

  const errors = {
    current: hasPassword && !values.current ? 'Enter your current password' : '',
    next:
      passwordProblem(values.next) ||
      (hasPassword && values.next && values.next === values.current
        ? 'Choose a password different from your current one'
        : ''),
    confirm: values.confirm !== values.next ? "Passwords don't match" : '',
  };
  const invalid = Boolean(errors.current || errors.next || errors.confirm);
  const showErr = (k: 'current' | 'next' | 'confirm') => touched && Boolean(errors[k]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (invalid || saving || cooldown.active) return;
    setServerErrors({});
    setSaving(true);
    try {
      if (hasPassword) {
        await apiService.post('/auth/change-password', {
          currentPassword: values.current,
          newPassword: values.next,
        });
        notify('Password changed. Your other devices were signed out.', 'success');
        onChanged();
      } else {
        await apiService.post('/auth/set-password', { newPassword: values.next });
        notify('Password set. You can now also sign in with your email and password.', 'success');
        onPasswordSet();
        onChanged();
      }
      close();
    } catch (error: any) {
      const wait = retryAfterSeconds(error);
      const status = error?.response?.status;
      if (wait) {
        cooldown.start(wait);
        setServerErrors({ form: `Too many attempts. Try again in ${formatWait(wait)}.` });
      } else if (status === 401) {
        setServerErrors({ current: 'Current password is incorrect' });
      } else if (status === 400) {
        setServerErrors({ next: extractError(error, 'That password isn’t allowed') });
      } else if (!error?.response) {
        setServerErrors({
          form: "Couldn't reach the server. Check your connection and try again.",
        });
      } else {
        setServerErrors({ form: extractError(error, 'Failed to update password') });
      }
    } finally {
      setSaving(false);
    }
  };

  const eyeFor = (k: keyof typeof EMPTY, label: string) => (
    <InputAdornment position="end">
      <IconButton
        size="small"
        onClick={() => setShow((v) => ({ ...v, [k]: !v[k] }))}
        edge="end"
        tabIndex={-1}
        aria-label={show[k] ? `Hide ${label}` : `Show ${label}`}
      >
        {show[k] ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
      </IconButton>
    </InputAdornment>
  );
  const typeFor = (k: keyof typeof EMPTY) => (show[k] ? 'text' : 'password');
  const setField = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    // Editing a field clears the server's complaint about it.
    if (k === 'current' && serverErrors.current) setServerErrors((s) => ({ ...s, current: '' }));
    if (k === 'next' && serverErrors.next) setServerErrors((s) => ({ ...s, next: '' }));
  };

  const title = hasPassword ? 'Password' : 'Set a password';
  const subtitle = hasPassword
    ? 'Change your sign-in password.'
    : 'You signed up with Google and have no password yet. Set one to also sign in with email and password.';

  return (
    <AccountCard
      icon={<KeyRound size={18} strokeWidth={1.75} />}
      title={title}
      subtitle={subtitle}
      {...(open
        ? {
            onSubmit: submit,
            onEscape: close,
            footer: (
              <>
                <Button
                  onClick={close}
                  disabled={saving}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving || cooldown.active}
                  sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
                >
                  {saving ? (
                    <CircularProgress size={18} sx={{ color: 'inherit' }} />
                  ) : cooldown.active ? (
                    `Try again in ${formatWait(cooldown.remaining)}`
                  ) : hasPassword ? (
                    'Update password'
                  ) : (
                    'Set password'
                  )}
                </Button>
              </>
            ),
          }
        : {
            action: (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'divider',
                  color: 'text.primary',
                }}
              >
                {hasPassword ? 'Change password' : 'Set password'}
              </Button>
            ),
          })}
    >
      {open ? (
        <Stack spacing={2}>
          {serverErrors.form && <Alert severity="error">{serverErrors.form}</Alert>}
          {hasPassword && (
            <TextField
              label="Current password"
              type={typeFor('current')}
              value={values.current}
              onChange={setField('current')}
              onFocus={() => setCurrentUnlocked(true)}
              autoComplete="off"
              autoFocus
              fullWidth
              error={showErr('current') || Boolean(serverErrors.current)}
              helperText={(showErr('current') && errors.current) || serverErrors.current || ' '}
              InputProps={{
                readOnly: !currentUnlocked,
                endAdornment: eyeFor('current', 'current password'),
              }}
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="New password"
              type={typeFor('next')}
              value={values.next}
              onChange={setField('next')}
              autoComplete="new-password"
              {...(hasPassword ? {} : { autoFocus: true })}
              fullWidth
              error={showErr('next') || Boolean(serverErrors.next)}
              helperText={
                (showErr('next') && errors.next) ||
                serverErrors.next ||
                `At least ${PASSWORD_MIN} characters`
              }
              InputProps={{ endAdornment: eyeFor('next', 'new password') }}
            />
            <TextField
              label="Confirm new password"
              type={typeFor('confirm')}
              value={values.confirm}
              onChange={setField('confirm')}
              autoComplete="new-password"
              fullWidth
              error={showErr('confirm')}
              helperText={showErr('confirm') ? errors.confirm : ' '}
              InputProps={{ endAdornment: eyeFor('confirm', 'confirmation') }}
            />
          </Stack>
        </Stack>
      ) : (
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', lineHeight: 1.6 }}>
          {hasPassword
            ? 'Use a password you don’t use anywhere else. Changing it signs you out of your other devices.'
            : 'Until you set one, you can only sign in with Google.'}
        </Typography>
      )}
    </AccountCard>
  );
};

const SecurityPanel: React.FC<Props> = (props) => {
  // Bumped after a password change so the devices list re-fetches (the server just
  // ended every other session).
  const [sessionsKey, setSessionsKey] = useState(0);
  return (
    <Stack spacing={3}>
      <PasswordCard {...props} onChanged={() => setSessionsKey((k) => k + 1)} />
      <AccountCard
        icon={<MonitorSmartphone size={18} strokeWidth={1.75} />}
        title="Signed-in devices"
        subtitle="Where your account is signed in. Sign out anything you don’t recognise."
      >
        <ActiveSessions key={sessionsKey} mode="self" />
      </AccountCard>
    </Stack>
  );
};

export default SecurityPanel;
